import { ChangeEvent, FC } from "react"
import CurrencyFormat from "react-currency-format"

export type InputRangeWithFieldProps = {
  /**
   * Component id
   */
  id: string
  /**
   * Text to be used as label and placeholder
   */
  label: string
  /**
   * Indicates if input field should be read only
   */
  readOnly?: boolean
  /**
   * Input value
   */
  value: any
  error?: string
  touched?: boolean
  /**
   * Input type
   */
  type?: "number" | "currency" | "text" | "percentage"
  /**
   * Minimum possible value to assign to the input
   */
  min?: number
  /**
   * Maximum possible value to assign to the input
   */
  max?: number
  /**
   * The step with which the range input should increment
   */
  step?: number | "any"
  /**
   * Function to call when input value changes
   */
  handleChange?: (event: ChangeEvent<HTMLInputElement>) => void
}

export const InputRangeWithField: FC<InputRangeWithFieldProps> = ({
  id,
  label,
  readOnly = false,
  error = "",
  touched = false,
  type = "number",
  min = 1,
  max = 100,
  step = 1,
  value = min,
  handleChange = () => {},
}) => {
  return (
    <div className="w-full">
      <div className={`flex justify-between ${readOnly ? "items-start flex-col" : "items-center flex-row"}`}>
        <label htmlFor={id} className="block text-sm px-3 font-medium text-semibold text-gray-700">
          {label}
        </label>

        {type === "number" || type === "text" ? (
          <input
            id={id}
            name={id}
            type={type}
            placeholder={label}
            readOnly={readOnly}
            value={value}
            min={min}
            max={max}
            className={`block min-w-[50%] h-10 rounded-md border-0 py-2 px-3 text-left shadow-lg transition focus:border-brand-light focus:ring-brand-light sm:text-sm ${
              readOnly ? "mt-1" : ""
            }`}
            onChange={readOnly ? () => {} : handleChange}
          />
        ) : (
          <CurrencyFormat
            id={id}
            name={id}
            placeholder={label}
            readOnly={readOnly}
            value={value}
            className={`block min-w-[50%] h-10 rounded-md border-0 py-2 px-3 text-left shadow-lg transition focus:border-brand-light focus:ring-brand-light sm:text-sm ${
              readOnly ? "mt-1" : ""
            }`}
            displayType={"input"}
            onChange={readOnly ? () => {} : handleChange}
            prefix={type === "currency" ? "$" : undefined}
            suffix={type === "percentage" ? " %" : undefined}
            thousandSeparator
            decimalScale={5}
            min={min}
            max={max}
          />
        )}
      </div>

      {!readOnly && (
        <div className="mt-1 rounded-md border-0 shadow-lg bg-white overflow-hidden">
          <input
            id={id}
            name={id}
            type="range"
            placeholder={label}
            readOnly={readOnly}
            value={value}
            min={min}
            max={max}
            step={step}
            className="range-input block w-full h-10 py-2 px-3 text-left transition focus:border-brand-light focus:ring-brand-light sm:text-sm"
            onChange={readOnly ? () => {} : handleChange}
            //@ts-ignore
            style={{ "--progress": String((value - min) / (max - min)) }}
          />
          <div className="w-full px-3 text-xs flex justify-between -mt-2 pb-1">
            <div>
              {min} {type === "percentage" && "%"}
            </div>
            <div>
              {max} {type === "percentage" && "%"}
            </div>
          </div>
        </div>
      )}
      {touched && error && <span className="text-red-500">{error}</span>}
    </div>
  )
}

